import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import UndoOutlined from '@ant-design/icons/UndoOutlined';
import EnvironmentOutlined from '@ant-design/icons/EnvironmentOutlined';
import AimOutlined from '@ant-design/icons/AimOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import MinusOutlined from '@ant-design/icons/MinusOutlined';

import { OpTooltip } from 'new-components/DLS/OpTooltip/OpTooltip';
import { OpSection } from 'new-components/DLS/OpSection/OpSection';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';

import { OpForm } from '../../OpForm';
import { useOpFormContext } from '../../OpFormContext';

import './OpFormMapGeofence.scss';

interface MapGeofenceComponentProps {
  value?: string;
  onChange?: (value: string) => void;
  zipCode?: string;
  defaultCenter?: LatLng;
  height?: number;
  disabled?: boolean;
}

interface LatLng {
  lat: number;
  lng: number;
}

interface OpFormMapGeofenceProps
  extends Pick<
    ComponentProps<typeof OpForm.Item>,
    | 'name'
    | 'label'
    | 'rules'
    | 'tooltip'
    | 'validateTrigger'
    | 'initialValue'
    | 'validateStatus'
    | 'help'
    | 'required'
  > {
  zipCode?: string;
  defaultCenter?: LatLng;
  height?: number;
  disabled?: boolean;
}

// Utility functions for WKT conversion
const pointsToWKT = (points: LatLng[]): string => {
  if (points.length < 3) return '';

  const closedPoints = [...points, points[0]];
  const coordinateString = closedPoints
    .map((point) => `${point.lng} ${point.lat}`)
    .join(', ');

  return `POLYGON((${coordinateString}))`;
};

const wktToPoints = (wkt: string): LatLng[] => {
  if (!wkt || !wkt.startsWith('POLYGON((') || !wkt.endsWith('))')) {
    return [];
  }

  const coordinateString = wkt.slice(9, -2);
  const coordinates = coordinateString.split(', ');

  return coordinates.slice(0, -1).map((coord) => {
    const [lng, lat] = coord.split(' ').map(Number);
    return { lat, lng };
  });
};

// Geocoding function for zip codes
// NOTE: When we move to international, we will need to pass the city and country as well (e.g 82630-143 Curitiba PR Brazil)
const geocodeZipCode = async (
  zipCode: string,
  country = 'USA',
): Promise<LatLng | null> => {
  try {
    // Try the most specific query first
    const query = `${zipCode}, ${country}`;
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
    );
    const data = await response.json();

    if (data && data.length > 0) {
      const result = data[0];
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
      };
    }
  } catch (error) {
    console.warn('Geocoding failed:', error);
  }
  return null;
};

// Get current location
const getCurrentLocation = (): Promise<LatLng | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        resolve(null);
      },
      {
        timeout: 10000,
        maximumAge: 300000,
      },
    );
  });
};

// Internal component that handles the map functionality
const MapGeofenceComponent = ({
  value = '',
  onChange,
  zipCode,
  defaultCenter = { lat: 39.8283, lng: -98.5795 },
  height = 400,
  disabled = false,
}: MapGeofenceComponentProps) => {
  const { t } = useTranslation();
  const { isDataLoading, isReadOnly } = useOpFormContext();

  // Map-related refs and state
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // Use refs to avoid stale closures
  const isDrawingRef = useRef(false);
  const disabledRef = useRef(false);
  const pointsRef = useRef<LatLng[]>([]);

  // Form-related state
  const [mapCenter, setMapCenter] = useState<LatLng>(defaultCenter);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [points, setPoints] = useState<LatLng[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldAutoZoom, setShouldAutoZoom] = useState(true);

  // Handler functions
  const handlePointAdd = useCallback((newPoint: LatLng) => {
    setPoints((prev) => [...prev, newPoint]);
  }, []);

  const handleFinishDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  // Map initialization effect
  useEffect(() => {
    let mounted = true;

    const loadMapScript = () => {
      return new Promise<void>((resolve, reject) => {
        // Check if Leaflet is already loaded
        if (typeof window !== 'undefined' && (window as any).L) {
          resolve();
          return;
        }

        // Load CSS first
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }

        // Load JavaScript
        if (!document.querySelector('script[src*="leaflet.js"]')) {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Leaflet'));
          document.head.appendChild(script);
        } else {
          resolve();
        }
      });
    };

    const initMap = async () => {
      if (!mapRef.current || mapInstanceRef.current || !mounted) {
        return;
      }

      try {
        // Load Leaflet script
        await loadMapScript();

        if (!mounted) return;

        // Get Leaflet from window
        const L = (window as any).L;
        if (!L) {
          throw new Error('Leaflet not available');
        }

        // Wait a moment for everything to be ready
        await new Promise((resolve) => {
          setTimeout(resolve, 300);
        });

        if (!mounted) return;

        // Initialize map
        const map = L.map(mapRef.current, {
          zoomControl: true,
          attributionControl: true,
          scrollWheelZoom: false,
        }).setView([mapCenter.lat, mapCenter.lng], 13);

        // Add tile layer
        const tileLayer = L.tileLayer(
          'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
          },
        );

        tileLayer.addTo(map);

        // Force a resize to ensure proper rendering
        setTimeout(() => {
          if (mounted && map) {
            map.invalidateSize();
          }
        }, 100);

        if (mounted) {
          mapInstanceRef.current = map;
          setIsMapReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize map:', error);
        if (mounted) {
          setMapError(
            error instanceof Error ? error.message : 'Failed to load map',
          );
          setIsMapReady(true);
        }
      }
    };

    // Add delay to ensure DOM is ready and retry if needed
    let retryCount = 0;
    const maxRetries = 10;

    const tryInitMap = () => {
      if (mapRef.current && mounted) {
        initMap();
      } else if (retryCount < maxRetries - 1) {
        retryCount++;
        setTimeout(tryInitMap, 200);
      } else if (mounted) {
        setMapError('Failed to initialize map: DOM element not found');
        setIsMapReady(true);
      }
    };

    const timeoutId = setTimeout(tryInitMap, 100);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (error) {
          console.error('Error cleaning up map:', error);
        }
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Can't add dependencies here because it will cause a loop

  // Update map center
  useEffect(() => {
    if (mapInstanceRef.current && isMapReady) {
      mapInstanceRef.current.setView(
        [mapCenter.lat, mapCenter.lng],
        mapInstanceRef.current.getZoom(),
      );
    }
  }, [mapCenter, isMapReady]);

  // Update polygon and markers
  useEffect(() => {
    if (!mapInstanceRef.current || !isMapReady) {
      return;
    }

    const L = (window as any).L;
    if (!L) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markersRef.current = [];

    // Clear existing polygon
    if (polygonRef.current) {
      mapInstanceRef.current.removeLayer(polygonRef.current);
      polygonRef.current = null;
    }

    // Add markers for each point
    points.forEach((point, index) => {
      const marker = L.marker([point.lat, point.lng]).addTo(
        mapInstanceRef.current,
      );
      marker.bindPopup(`Point ${index + 1}`);
      markersRef.current.push(marker);
    });

    // Add polygon if we have enough points
    if (points.length >= 3) {
      const polygon = L.polygon(
        points.map((p) => [p.lat, p.lng]),
        {
          color: '#1890ff',
          fillColor: '#1890ff',
          fillOpacity: 0.2,
          weight: 2,
        },
      ).addTo(mapInstanceRef.current);
      polygonRef.current = polygon;
    }

    // Fit map to show all points with some padding - only on initial load, not when user is drawing
    if (points.length > 0 && shouldAutoZoom) {
      const group = new L.FeatureGroup(markersRef.current);
      if (polygonRef.current) {
        group.addLayer(polygonRef.current);
      }
      mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
      // Disable auto-zoom after the first time we zoom to points
      setShouldAutoZoom(false);
    }
  }, [points, isMapReady, shouldAutoZoom]);

  // Update refs when state changes
  useEffect(() => {
    isDrawingRef.current = isDrawing;
    disabledRef.current = disabled;
    pointsRef.current = points;
  }, [isDrawing, disabled, points]);

  // Update click handler when drawing state changes
  useEffect(() => {
    if (!mapInstanceRef.current || !isMapReady) return;

    // Remove old click handlers
    if (mapInstanceRef.current._clickHandler) {
      mapInstanceRef.current.off('click', mapInstanceRef.current._clickHandler);
    }
    if (mapInstanceRef.current._dblClickHandler) {
      mapInstanceRef.current.off(
        'dblclick',
        mapInstanceRef.current._dblClickHandler,
      );
    }

    // Add new click handler with current state
    const clickHandler = (e: any) => {
      if (isDrawingRef.current && !disabledRef.current) {
        handlePointAdd({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    };

    // Add double-click handler to finish drawing
    const dblClickHandler = (_e: any) => {
      if (
        isDrawingRef.current &&
        !disabledRef.current &&
        pointsRef.current.length >= 3
      ) {
        handleFinishDrawing();
      }
    };

    mapInstanceRef.current.on('click', clickHandler);
    mapInstanceRef.current.on('dblclick', dblClickHandler);
    mapInstanceRef.current._clickHandler = clickHandler;
    mapInstanceRef.current._dblClickHandler = dblClickHandler;
  }, [isDrawing, disabled, isMapReady, handlePointAdd, handleFinishDrawing]);

  // Initialize map center based on existing points, zipCode, or current location
  useEffect(() => {
    const initializeLocation = async () => {
      setIsLoadingLocation(true);

      try {
        // If we have existing points from form value, center on those
        if (value && typeof value === 'string') {
          const initialPoints = wktToPoints(value);
          if (initialPoints.length > 0) {
            // Set the points first, the auto-fit will be handled by the points effect
            setPoints(initialPoints);
            // Set a rough center for initial map load
            const avgLat =
              initialPoints.reduce((sum, p) => sum + p.lat, 0) /
              initialPoints.length;
            const avgLng =
              initialPoints.reduce((sum, p) => sum + p.lng, 0) /
              initialPoints.length;
            setMapCenter({ lat: avgLat, lng: avgLng });
            setIsLoadingLocation(false);
            return;
          }
        }

        // Fall back to zip code geocoding
        if (zipCode) {
          const zipLocation = await geocodeZipCode(zipCode);
          if (zipLocation) {
            setMapCenter(zipLocation);
            setIsLoadingLocation(false);
            return;
          }
        }

        // Fall back to current location
        const currentLocation = await getCurrentLocation();
        if (currentLocation) {
          setMapCenter(currentLocation);
        }
      } catch (error) {
        console.warn('Failed to get location:', error);
      } finally {
        setIsLoadingLocation(false);
      }
    };

    initializeLocation();
  }, [zipCode, value]);

  // Initialize from form value
  useEffect(() => {
    // Mark component as initialized after processing initial value
    setIsInitialized(true);
  }, [value]);

  // Update form value when points change
  useEffect(() => {
    const wktValue = points.length >= 3 ? pointsToWKT(points) : '';

    // Only call onChange if component has been initialized (not on initial load)
    if (wktValue !== value && onChange && isInitialized) {
      onChange(wktValue);
    }
  }, [points, value, onChange, isInitialized]);

  const handleStartDrawing = () => {
    setPoints([]);
    setIsDrawing(true);
    setShouldAutoZoom(false); // Disable auto-zoom when user starts drawing
  };

  const handleUndo = () => {
    setPoints((prev) => {
      const newPoints = prev.slice(0, -1);
      // If we still have points after undo, keep drawing active
      if (newPoints.length > 0) {
        setIsDrawing(true);
      }
      return newPoints;
    });
  };

  const handleClear = () => {
    setPoints([]);
    setIsDrawing(false);
    if (onChange) {
      onChange('');
    }
  };

  const handleUseCurrentLocation = async () => {
    setIsLoadingLocation(true);
    const location = await getCurrentLocation();
    if (location) {
      setMapCenter(location);
      setShouldAutoZoom(false); // Disable auto-zoom when user uses current location
    }
    setIsLoadingLocation(false);
  };

  const isDisabled = disabled || isReadOnly || isDataLoading;

  const headerContent = (
    <div className="op-form-map-geofence__header">
      <div className="op-form-map-geofence__header-left">
        <OpTooltip title={t('Start drawing')}>
          <OpButton
            type="text"
            icon={<EnvironmentOutlined />}
            onClick={handleStartDrawing}
            disabled={isDisabled || isDrawing}
            className="op-form-map-geofence__header-icon-button"
            gtm="op-form-map-geofence-start-drawing-button"
          />
        </OpTooltip>
        <OpTooltip title={t('Use current location')}>
          <OpButton
            type="text"
            icon={<AimOutlined />}
            onClick={handleUseCurrentLocation}
            loading={isLoadingLocation}
            disabled={isDisabled}
            className="op-form-map-geofence__header-icon-button"
          />
        </OpTooltip>
      </div>

      {/* <div className="op-form-map-geofence__header-center">
        <span className="op-form-map-geofence__title">
          {t("Draw your site's boundary")}
          <span className="op-form-map-geofence__required">*</span>
        </span>
        <QuestionCircleOutlined className="op-form-map-geofence__help-icon" />
      </div> */}

      <div className="op-form-map-geofence__header-right">
        <OpTooltip title={t('Undo last point')}>
          <OpButton
            type="text"
            icon={<UndoOutlined />}
            onClick={handleUndo}
            disabled={isDisabled || points.length === 0}
            className="op-form-map-geofence__header-icon-button"
          />
        </OpTooltip>
        <OpTooltip title={t('Clear all points')}>
          <OpButton
            type="text"
            icon={<DeleteOutlined />}
            onClick={handleClear}
            disabled={isDisabled || points.length === 0}
            className="op-form-map-geofence__header-icon-button"
          />
        </OpTooltip>
      </div>
    </div>
  );

  return (
    <OpSection className="op-form-map-geofence">
      <div className="op-form-map-geofence__header">{headerContent}</div>
      {/* Map container with zoom controls */}
      <div className="op-form-map-geofence__map-container">
        <div
          ref={mapRef}
          style={{
            height: `${height}px`,
            width: '100%',
            backgroundColor:
              isMapReady && !mapError
                ? 'var(--colorFillTertiary)'
                : 'var(--colorFillQuaternary)',
            border:
              isMapReady && !mapError
                ? '1px solid var(--colorBorder)'
                : '2px dashed var(--colorBorder)',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className={clsx('op-form-map-geofence__map', {
            'op-form-map-geofence__map--drawing': isDrawing,
            'op-form-map-geofence__map--disabled': isDisabled,
          })}
        >
          {/* Zoom controls */}
          <div className="op-form-map-geofence__zoom-controls">
            <OpButton
              type="text"
              icon={<PlusOutlined />}
              onClick={() => mapInstanceRef.current?.zoomIn()}
              disabled={isDisabled}
              className="op-form-map-geofence__zoom-button"
            />
            <OpButton
              type="text"
              icon={<MinusOutlined />}
              onClick={() => mapInstanceRef.current?.zoomOut()}
              disabled={isDisabled}
              className="op-form-map-geofence__zoom-button"
            />
          </div>

          {!isMapReady ? (
            <div style={{ textAlign: 'center' }}>
              <OpSkeleton.Input
                active
                style={{
                  height: '40px',
                  width: '200px',
                  marginBottom: '10px',
                }}
              />
              <div
                style={{ fontSize: '14px', color: 'var(--colorTextTertiary)' }}
              >
                {t('Loading map...')}
              </div>
            </div>
          ) : mapError ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div
                style={{
                  fontSize: '16px',
                  color: 'var(--colorError)',
                  marginBottom: '10px',
                }}
              >
                {t('Map failed to load')}
              </div>
              <div
                style={{ fontSize: '12px', color: 'var(--colorTextTertiary)' }}
              >
                {mapError}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Drawing instruction */}
      {/* {isDrawing && (
        <div className="op-form-map-geofence__instruction">
          <span className="op-form-map-geofence__instruction-text">
            {t('Click to add points')}
          </span>
        </div>
      )} */}
    </OpSection>
  );
};

export const OpFormMapGeofence = ({
  name,
  label,
  rules,
  tooltip,
  validateTrigger,
  initialValue,
  validateStatus,
  help,
  required,
  zipCode,
  defaultCenter = { lat: 39.8283, lng: -98.5795 },
  height = 400,
  disabled = false,
  ...elementProps
}: OpFormMapGeofenceProps) => {
  const { isDataLoading } = useOpFormContext();
  const [uniqueId] = useState(() => uuidv4());

  if (isDataLoading) {
    return (
      <OpSkeleton
        loading
        paragraph={{ rows: 8 }}
        style={{ height: height + 50 }}
      />
    );
  }

  return (
    <OpForm.Item
      name={name}
      label={label}
      rules={rules}
      tooltip={tooltip}
      validateTrigger={validateTrigger}
      initialValue={initialValue}
      validateStatus={validateStatus}
      help={help}
      required={required}
      {...(!name && { htmlFor: uniqueId })}
      {...elementProps}
    >
      <MapGeofenceComponent
        zipCode={zipCode}
        defaultCenter={defaultCenter}
        height={height}
        disabled={disabled}
      />
    </OpForm.Item>
  );
};
